import type { ViewAxis, ViewLock, ViewSign } from "../app/storeTypes.ts";

/**
 * Angle (radians) of deviation from the locked axis direction that breaks the
 * orthographic lock. 1.25° — responsive enough that a normal rotate drag
 * breaks out quickly, while still rejecting incidental drift.
 */
export const VIEW_LOCK_BREAK_ANGLE_RAD = (1.25 * Math.PI) / 180;

/**
 * Unit vector from target to camera for each of the six axis-aligned views.
 * Matches Blender's Z-up convention (front = camera at −Y looking +Y, etc.).
 */
export function viewDirection(
  axis: ViewAxis,
  sign: ViewSign,
): [number, number, number] {
  switch (axis) {
    case "x":
      return [sign, 0, 0];
    case "y":
      return [0, sign, 0];
    case "z":
      return [0, 0, sign];
  }
}

/**
 * Camera up vector for each axis-aligned view. Matches Blender:
 * - Front/Back/Left/Right → +Z up
 * - Top  (looking −Z) → +Y up (X right, Y up on screen)
 * - Bottom (looking +Z) → −Y up (X right, Y down on screen — mirrored)
 */
export function viewUp(
  axis: ViewAxis,
  sign: ViewSign,
): [number, number, number] {
  if (axis === "z") return [0, sign, 0];
  return [0, 0, 1];
}

/**
 * Human-readable label for the view. Blender names.
 */
export function viewLabel(axis: ViewAxis, sign: ViewSign): string {
  if (axis === "x") return sign > 0 ? "Right" : "Left";
  if (axis === "y") return sign > 0 ? "Back" : "Front";
  return sign > 0 ? "Top" : "Bottom";
}

/**
 * Compute the camera position for an axis-aligned view orbiting `target` at
 * `distance`.
 */
export function viewPosition(
  axis: ViewAxis,
  sign: ViewSign,
  target: [number, number, number],
  distance: number,
): [number, number, number] {
  const dir = viewDirection(axis, sign);
  return [
    target[0] + dir[0] * distance,
    target[1] + dir[1] * distance,
    target[2] + dir[2] * distance,
  ];
}

/**
 * Angle (radians) between the current camera→target direction and the locked
 * view's canonical axis.
 */
export function viewDeviationAngle(
  axis: ViewAxis,
  sign: ViewSign,
  position: [number, number, number] | { x: number; y: number; z: number },
  target: [number, number, number] | { x: number; y: number; z: number },
): number {
  const px = Array.isArray(position) ? position[0] : position.x;
  const py = Array.isArray(position) ? position[1] : position.y;
  const pz = Array.isArray(position) ? position[2] : position.z;
  const tx = Array.isArray(target) ? target[0] : target.x;
  const ty = Array.isArray(target) ? target[1] : target.y;
  const tz = Array.isArray(target) ? target[2] : target.z;

  const dx = px - tx;
  const dy = py - ty;
  const dz = pz - tz;
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (len < 1e-9) return 0;

  const [ax, ay, az] = viewDirection(axis, sign);
  const cos = Math.max(-1, Math.min(1, (dx * ax + dy * ay + dz * az) / len));
  return Math.acos(cos);
}

/**
 * Whether the next authoritative camera pose breaks the current axis lock.
 * Pan and dolly preserve the locked direction, so they remain locked; only
 * writes that actually steer the camera off-axis invalidate the lock.
 */
export function viewLockBreakReason(
  lock: ViewLock | null,
  position: [number, number, number],
  target: [number, number, number],
): "rotation" | null {
  if (!lock) return null;
  const angle = viewDeviationAngle(lock.axis, lock.sign, position, target);
  return angle > VIEW_LOCK_BREAK_ANGLE_RAD ? "rotation" : null;
}

/**
 * Seed the orthographic half-height for entering an axis view or switching to
 * orthographic directly. When already orthographic we preserve the current zoom.
 */
export function lockedOrthographicHalfHeight(
  distance: number,
  focalLength: number,
  currentProjection: "perspective" | "orthographic",
  currentHalfHeight: number,
  alreadyLocked: boolean,
): number {
  if (alreadyLocked || currentProjection === "orthographic")
    return currentHalfHeight;
  const fovRad = 2 * Math.atan(12 / focalLength); // 24 mm sensor, halved
  return Math.max(0.01, Math.min(500, distance * Math.tan(fovRad / 2)));
}
